/*!

=========================================================
* BootstrapVue Argon Dashboard - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/bootstrap-vue-argon-dashboard
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Vue from "vue";
import money from "v-money";
import DashboardPlugin from "./plugins/dashboard-plugin";
import App from "./App.vue";
import titleMixin from "./mixins/titleMixin";
import VueSweetalert2 from "vue-sweetalert2";
import Notifications from "vue-notification";
import i18n from "./i18n";
import dotenv from "dotenv";

dotenv.config();

// router setup
import router from "./routes/router";
import "@/assets/css/new-deposit.css";
// import '@/assets/vendor/flag/css/flags32-both.css';
import "sweetalert2/dist/sweetalert2.min.css";
import "@fortawesome/fontawesome-free/css/all.css";
import "@fortawesome/fontawesome-free/js/all.js";
import "@/assets/vendor/flag-icons/css/flag-icons.min.css";

const CURRENT_VERSION = process.env.VUE_APP_NOCACHE_VERSION;
const MERCHANT_THEMES = process.env.VUE_APP_MERCHANT_THEMES;

const savedVersion = localStorage.getItem("app_version");

if (savedVersion && savedVersion !== CURRENT_VERSION) {
  localStorage.setItem("app_version", CURRENT_VERSION);
  window.location.reload(true); // reload paksa
} else {
  localStorage.setItem("app_version", CURRENT_VERSION);
}

// ✅ ADD: Show loading screen immediately
function showLoadingScreen() {
  const loadingHTML = `
    <div id="app-loading" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      flex-direction: column;
    ">
      <div style="
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        animation: spin 2s linear infinite;
        margin-bottom: 20px;
      "></div>
      <p style="color: #666; font-family: Arial, sans-serif;">Loading...</p>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    </div>
  `;

  document.body.insertAdjacentHTML("beforeend", loadingHTML);
}

function hideLoadingScreen() {
  const loadingEl = document.getElementById("app-loading");
  if (loadingEl) {
    loadingEl.remove();
  }
}

// Show loading immediately
showLoadingScreen();

// === DYNAMIC CSS loader dengan perbaikan ===
function loadRoleCss(role, force = false) {
  console.log(`🎨 Loading CSS for role: ${role}${force ? " (forced)" : ""}`);

  // Validasi role input
  if (role !== "member" && role !== "partner") {
    console.error("❌ Invalid role provided to loadRoleCss:", role);
    role = "member"; // Default fallback
  }

  const currentRole = document.body.getAttribute("data-role");

  // Jika role sama dan tidak force, skip loading
  if (!force && currentRole === role) {
    console.log("✅ CSS already loaded for role:", role);
    return Promise.resolve();
  }

  // ✅ CRITICAL FIX: Set body classes IMMEDIATELY before CSS loading
  setBodyClasses(role);

  // Set data attribute untuk tracking
  document.body.setAttribute("data-role", role);

  // Hapus CSS lama (jika ada)
  ["role-style-1", "role-style-2"].forEach((id) => {
    const oldLink = document.getElementById(id);
    if (oldLink) {
      console.log(`🗑️ Removing old CSS: ${id}`);
      oldLink.remove();
    }
  });

  // Load CSS promises
  const cssPromises = [];

  // Tambah CSS baru sesuai role dengan error handling
  if (role === "partner") {
    console.log("🌙 Loading partner CSS theme (dark mode)");
    cssPromises.push(
      appendCss(
        "/css/partner-main.css?version=" + CURRENT_VERSION,
        "role-style-1"
      )
    );
    cssPromises.push(
      appendCss(
        "/css/partner-transfer.css?version=" + CURRENT_VERSION,
        "role-style-2"
      )
    );
  } else {
    console.log("☀️ Loading member CSS theme (light mode)");
    cssPromises.push(
      appendCss("/css/new-main.css?version=" + CURRENT_VERSION, "role-style-1")
    );
    if(MERCHANT_THEMES){
      cssPromises.push(
        appendCss("/css/"+MERCHANT_THEMES+"?version=" + CURRENT_VERSION, "role-style-1")
      );
    }
    cssPromises.push(
      appendCss("/css/transfer.css?version=" + CURRENT_VERSION, "role-style-2")
    );
  }

  // Return promise untuk semua CSS loading
  return Promise.all(cssPromises)
    .then(() => {
      console.log(`✅ All CSS loaded successfully for role: ${role}`);

      // ✅ ENSURE: Re-apply body classes after CSS loads
      setBodyClasses(role);

      // Emit custom event untuk components
      window.dispatchEvent(
        new CustomEvent("cssLoaded", {
          detail: { role: role, timestamp: Date.now() },
        })
      );
    })
    .catch((error) => {
      console.error(`❌ Error loading CSS for role ${role}:`, error);

      // ✅ FALLBACK: Still set body classes even if CSS fails
      setBodyClasses(role);

      // Emit error event
      window.dispatchEvent(
        new CustomEvent("cssLoadError", {
          detail: { role: role, error: error.message },
        })
      );
    });
}

// ✅ NEW: Separate function to set body classes
function setBodyClasses(role) {
  if (role === "partner") {
    document.body.classList.add("partner-theme");
    document.body.classList.remove("member-theme");
  } else {
    document.body.classList.add("member-theme");
    document.body.classList.remove("partner-theme");
  }
}

function appendCss(href, id) {
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.id = id;

    // Add timeout untuk loading
    const timeout = setTimeout(() => {
      const error = new Error(`CSS loading timeout: ${id}`);
      console.error("⏰", error.message);
      reject(error);
    }, 10000); // 10 second timeout

    // Add load event listener
    link.onload = () => {
      clearTimeout(timeout);
      console.log(`✅ CSS loaded successfully: ${id}`);
      // ✅ Clear temporary background color after CSS loads
      setTimeout(() => {
        document.body.style.backgroundColor = "";
      }, 100);
      resolve();
    };

    link.onerror = () => {
      clearTimeout(timeout);
      const error = new Error(`Failed to load CSS: ${id} from ${href}`);
      console.error("❌", error.message);
      // ✅ Clear temporary background color even on error
      setTimeout(() => {
        document.body.style.backgroundColor = "";
      }, 100);
      reject(error);
    };

    document.head.appendChild(link);
  });
}

function initializeRole() {
  let roleItem = localStorage.getItem("role");
  const checkUser = localStorage.getItem("token");
  const currentPath = window.location.pathname;

  console.log(
    `🔧 Initializing role - Current: ${roleItem}, Token: ${!!checkUser}, Path: ${currentPath}`
  );

  // Auth pages harus selalu member theme
  const isAuthPage = [
    "/login",
    "/register",
    "/forgot-password",
    "/resend-email",
    "/maintenance",
  ].includes(currentPath);

  if (isAuthPage || !checkUser) {
    roleItem = "member";
    localStorage.setItem("role", "member");
    console.log("🔒 Auth page or no token detected, forcing member role");
  } else if (!roleItem || (roleItem !== "member" && roleItem !== "partner")) {
    // Jika role tidak valid, set default ke member
    roleItem = "member";
    localStorage.setItem("role", "member");
    console.log("⚠️ Invalid role detected, setting to member");
  }

  return roleItem;
}

// Initialize role
const initialRole = initializeRole();

// ✅ CRITICAL FIX: Set body classes IMMEDIATELY before any CSS loading
setBodyClasses(initialRole);

// === GLOBAL FUNCTIONS ===

// Global functions dengan perbaikan
window.loadRoleCss = loadRoleCss;

window.toggleRole = function () {
  const current = localStorage.getItem("role") || "member";
  const newRole = current === "partner" ? "member" : "partner";
  console.log(`🔄 Toggling role: ${current} -> ${newRole}`);

  localStorage.setItem("role", newRole);

  loadRoleCss(newRole)
    .then(() => {
      // Emit custom event untuk notify components
      window.dispatchEvent(
        new CustomEvent("roleChanged", {
          detail: {
            oldRole: current,
            newRole: newRole,
            source: "toggle",
            timestamp: Date.now(),
          },
        })
      );
    })
    .catch((error) => {
      console.error("❌ Error in role toggle:", error);
    });
};

window.syncRoleWithProfile = function () {
  const token = localStorage.getItem("token");
  if (!token) {
    console.log("🔐 No token, skipping role sync");
    return Promise.resolve();
  }

  console.log("🔄 Syncing role with server profile...");

  // Import Common service dynamically to avoid circular dependency
  return import("@/services/Common")
    .then((Common) => {
      return Common.default.profile();
    })
    .then((response) => {
      if (response.status === 200 && response.data.success) {
        const profile = response.data.data;
        const currentRole = localStorage.getItem("role");

        console.log(
          `👤 Profile loaded - partner_status: ${profile.partner_status}, current_role: ${currentRole}`
        );

        // Jika user bukan partner tapi role adalah partner, koreksi ke member
        if (profile.partner_status !== 1 && currentRole === "partner") {
          console.log("⚠️ User is not partner, correcting role to member");
          localStorage.setItem("role", "member");

          return loadRoleCss("member").then(() => {
            // Emit event untuk notify components
            window.dispatchEvent(
              new CustomEvent("roleChanged", {
                detail: {
                  oldRole: currentRole,
                  newRole: "member",
                  source: "profile_sync",
                  reason: "partner_status_invalid",
                },
              })
            );
          });
        }

        console.log("✅ Role sync completed successfully");
      }
    })
    .catch((err) => {
      console.error("❌ Error syncing role with profile:", err);
    });
};

window.setManualRole = function (newRole) {
  console.log(`🎯 Manual role change requested: ${newRole}`);

  // Validasi role
  if (newRole !== "member" && newRole !== "partner") {
    console.error("❌ Invalid role provided:", newRole);
    return;
  }

  const oldRole = localStorage.getItem("role");
  localStorage.setItem("role", newRole);

  loadRoleCss(newRole)
    .then(() => {
      console.log(
        `✅ Manual role changed successfully: ${oldRole} -> ${newRole}`
      );

      // Emit event untuk notify components
      window.dispatchEvent(
        new CustomEvent("roleChanged", {
          detail: {
            oldRole: oldRole,
            newRole: newRole,
            source: "manual",
            timestamp: Date.now(),
          },
        })
      );
    })
    .catch((error) => {
      console.error("❌ Error in manual role change:", error);
    });
};

// Utility functions
window.getCurrentRole = function () {
  const role = localStorage.getItem("role");
  if (role !== "member" && role !== "partner") {
    console.warn(
      `⚠️ Invalid role in localStorage: ${role}, defaulting to member`
    );
    localStorage.setItem("role", "member");
    return "member";
  }
  return role || "member";
};

window.checkCssLoadStatus = function () {
  const roleStyle1 = document.getElementById("role-style-1");
  const roleStyle2 = document.getElementById("role-style-2");
  const currentRole = localStorage.getItem("role");
  const bodyRole = document.body.getAttribute("data-role");

  const status = {
    currentRole: currentRole,
    bodyRole: bodyRole,
    roleStyle1Loaded: !!roleStyle1,
    roleStyle2Loaded: !!roleStyle2,
    bodyClasses: document.body.className,
    roleStyle1Href: roleStyle1 ? roleStyle1.href : null,
    roleStyle2Href: roleStyle2 ? roleStyle2.href : null,
  };

  console.log("🔍 CSS Load Status:", status);
  return status;
};

window.reloadCurrentRoleCSS = function () {
  const currentRole = window.getCurrentRole();
  console.log(`🔄 Force reloading CSS for current role: ${currentRole}`);
  return loadRoleCss(currentRole, true);
};

// === EVENT LISTENERS ===

// Cross-tab synchronization
window.addEventListener("storage", function (e) {
  if (e.key === "role" && e.newValue !== e.oldValue) {
    console.log(
      `🔗 Role changed in another tab: ${e.oldValue} -> ${e.newValue}`
    );
    if (e.newValue === "member" || e.newValue === "partner") {
      loadRoleCss(e.newValue).then(() => {
        // Emit event for local components
        window.dispatchEvent(
          new CustomEvent("roleChanged", {
            detail: {
              oldRole: e.oldValue,
              newRole: e.newValue,
              source: "cross_tab",
              timestamp: Date.now(),
            },
          })
        );
      });
    }
  }
});

// Page visibility change listener
document.addEventListener("visibilitychange", function () {
  if (!document.hidden) {
    // Page became visible, sync role with server
    console.log("👁️ Page became visible, syncing role...");
    window.syncRoleWithProfile();
  }
});

// DOM Content Loaded listener
document.addEventListener("DOMContentLoaded", function () {
  console.log("📄 DOM Content Loaded");

  setTimeout(() => {
    const currentRole = window.getCurrentRole();
    const roleStyle1 = document.getElementById("role-style-1");

    if (!roleStyle1) {
      console.warn("⚠️ CSS not loaded properly after DOM ready, reloading...");
      window.reloadCurrentRoleCSS();
    } else {
      console.log("✅ CSS validation passed after DOM ready");
    }
  }, 1000);
});

// Window load listener
window.addEventListener("load", function () {
  console.log("🪟 Window Loaded");

  setTimeout(() => {
    window.checkCssLoadStatus();

    // Sync with server profile after everything is loaded
    window.syncRoleWithProfile();
  }, 2000);
});

// === VUE SETUP ===

// plugin setup
Vue.use(DashboardPlugin);
Vue.use(money, { precision: 4 });
Vue.use(VueSweetalert2);
Vue.mixin(titleMixin);
Vue.use(Notifications);

Vue.directive("uppercase", {
  update: function (el) {
    el.value = el.value.toUpperCase();
    el.dispatchEvent(new Event("input"));
  },
});

// ✅ CRITICAL FIX: Load CSS BEFORE creating Vue instance
console.log(`🚀 Initializing app with role: ${initialRole}`);
loadRoleCss(initialRole)
  .then(() => {
    console.log("✅ Initial CSS loaded successfully");
    return initializeVueApp();
  })
  .catch((error) => {
    console.error("❌ Error loading initial CSS:", error);

    // Fallback: try loading member theme
    if (initialRole !== "member") {
      console.log("🔄 Fallback: Loading member theme...");
      localStorage.setItem("role", "member");
      return loadRoleCss("member", true).then(() => {
        return initializeVueApp();
      });
    } else {
      // Still initialize Vue even if CSS fails
      console.log("⚠️ Initializing Vue despite CSS error...");
      return initializeVueApp();
    }
  });

// ✅ NEW: Separate function to initialize Vue
function initializeVueApp() {
  console.log("🎯 Creating Vue instance...");

  // Hide loading screen
  hideLoadingScreen();

  /* eslint-disable no-new */
  const vueApp = new Vue({
    el: "#app",
    router,
    i18n,
    render: (h) => h(App),

    // Global error handler
    errorHandler: function (err, vm, info) {
      console.error("Vue Error:", err, info);
    },

    // Add global properties for role management
    created() {
      // Make role functions available in Vue components
      this.$roleManager = {
        getCurrentRole: window.getCurrentRole,
        setRole: window.setManualRole,
        toggleRole: window.toggleRole,
        syncWithProfile: window.syncRoleWithProfile,
        loadCSS: window.loadRoleCss,
        checkStatus: window.checkCssLoadStatus,
      };
    },
  });

  console.log("🎉 Vue application initialized successfully");

  // Export for testing purposes (development only)
  if (process.env.NODE_ENV === "development") {
    window.vueApp = vueApp;
    window.roleDebug = {
      loadRoleCss,
      initializeRole,
      checkCssLoadStatus: window.checkCssLoadStatus,
      getCurrentRole: window.getCurrentRole,
    };

    console.log(
      "🔧 Development mode: Role management functions exposed to window.roleDebug"
    );
  }

  return vueApp;
}
